import { type ReactElement, type ReactNode, cloneElement, isValidElement, useEffect, useId, useRef, useState } from 'react'

import type { FileObject, Room, User } from '../api/types'
import { previewUrl } from '../features/files/media'
import { dmPeer } from '../state/model'
import { useBackToClose } from './router'

const AVATAR_COLORS = ['#e57373', '#f06292', '#ba68c8', '#7986cb', '#4fc3f7', '#4db6ac', '#81c784', '#ffb74d', '#a1887f', '#90a4ae']

/** A photo's thumbnail cut to the avatar's shape (design 01a 2, 9), or
    what is drawn without it (none, or one that does not load). */
function PhotoOr(props: { photo: FileObject | null | undefined; size: number; children: ReactNode }) {
  const src = props.photo ? previewUrl(props.photo) : null
  const [broken, setBroken] = useState<string | null>(null)
  if (!src || src === broken) return <>{props.children}</>
  return (
    <span className="avatar photo" style={{ width: props.size, height: props.size }} aria-hidden>
      <img src={src} alt="" loading="lazy" decoding="async" draggable={false} onError={() => setBroken(src)} />
    </span>
  )
}

/** A person: their profile photo, or the first letter of the name. */
export function Avatar({ user, size = 40 }: { user: User | undefined; size?: number }) {
  const name = user?.name ?? '?'
  const color = AVATAR_COLORS[(user?.id ?? 0) % AVATAR_COLORS.length]
  const first = Array.from(name)[0] ?? '?'
  return (
    <PhotoOr photo={user?.photo} size={size}>
      <span className="avatar" style={{ width: size, height: size, background: color, fontSize: size * 0.42 }} aria-hidden>
        {first}
      </span>
    </PhotoOr>
  )
}

/** A room: a group room's photo, else someone in it; a 1:1 room shows the
    other person, my room of one ("나와의 채팅") me. */
export function RoomAvatar(props: { room: Room; users: Record<number, User>; meId: number; size?: number }) {
  const { room, users, meId, size = 46 } = props
  const other =
    room.kind === 'dm'
      ? dmPeer(room, meId)
      : room.kind === 'group'
        ? (room.members.find((m) => m.user_id !== meId)?.user_id ?? null)
        : null
  const person = <Avatar user={users[other ?? meId]} size={size} />
  return room.kind === 'group' ? (
    <PhotoOr photo={room.photo} size={size}>
      {person}
    </PhotoOr>
  ) : (
    person
  )
}

export function IconButton(props: {
  label: string
  onClick: () => void
  children: ReactNode
  className?: string
  disabled?: boolean
  /** an on/off button: shown as pressed while on */
  pressed?: boolean
}) {
  return (
    <button
      type="button"
      className={`icon-btn ${props.className ?? ''}`}
      aria-label={props.label}
      aria-pressed={props.pressed}
      title={props.label}
      onClick={props.onClick}
      disabled={props.disabled}
    >
      {props.children}
    </button>
  )
}

const ICONS = {
  back: 'M15 5l-7 7 7 7',
  plus: 'M12 5v14M5 12h14',
  gear: 'M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 11-4 0v-.1a1.7 1.7 0 00-1.1-1.5 1.7 1.7 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 110-4h.1a1.7 1.7 0 001.5-1.1 1.7 1.7 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.8.3H9a1.7 1.7 0 001-1.5V3a2 2 0 114 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.8V9a1.7 1.7 0 001.5 1H21a2 2 0 110 4h-.1a1.7 1.7 0 00-1.5 1z',
  more: 'M5 12h.01M12 12h.01M19 12h.01',
  send: 'M4 12l16-8-6 16-2-7-8-1z',
  close: 'M6 6l12 12M18 6L6 18',
  down: 'M6 9l6 6 6-6',
  'bell-off': 'M13.7 21a2 2 0 01-3.4 0M18.6 13A17.9 17.9 0 0118 8M6.3 6.3A6 6 0 006 8c0 7-3 9-3 9h14M18 8a6 6 0 00-9.3-5M2 2l20 20',
  retry: 'M4 4v6h6M20 20v-6h-6M20 9a8 8 0 00-14-3L4 10M4 15a8 8 0 0014 3l2-4',
  attach: 'M21.4 11l-9.2 9.2a6 6 0 01-8.5-8.5l9.2-9.2a4 4 0 015.7 5.7l-9.2 9.2a2 2 0 01-2.8-2.8l8.5-8.5',
  download: 'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3',
  upload: 'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12',
  photo: 'M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2zM8.5 10a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM21 15l-5-5L5 21',
  file: 'M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9zM13 2v7h7',
  folder: 'M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z',
  'folder-plus': 'M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2zM12 11v6M9 14h6',
  drive: 'M22 12H2M5.5 5.1L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.5-6.9A2 2 0 0016.8 4H7.2a2 2 0 00-1.7 1.1zM6 16h.01M10 16h.01',
  trash: 'M3 6h18M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6M10 11v6M14 11v6M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2',
  edit: 'M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4z',
  check: 'M5 12l5 5 9-11',
  prev: 'M15 5l-7 7 7 7',
  next: 'M9 5l7 7-7 7',
  chat: 'M21 11.5a8.4 8.4 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.4 8.4 0 01-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.4 8.4 0 013.8-.9h.5a8.5 8.5 0 018 8z',
  grid: 'M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z',
  list: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  calendar: 'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z',
  cart: 'M6 6h15l-1.5 9h-12zM6 6L5 3H2M9 20a1 1 0 100-2 1 1 0 000 2zM18 20a1 1 0 100-2 1 1 0 000 2z',
  pin: 'M12 21s-7-6.2-7-11a7 7 0 0114 0c0 4.8-7 11-7 11zM12 12.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z',
  megaphone: 'M3 11l18-5v12L3 14v-3zM11.6 16.8a3 3 0 11-5.8-1.6',
  poll: 'M4 20h16M7 16V9M12 16V4M17 16v-5',
  search: 'M11 18a7 7 0 100-14 7 7 0 000 14zM20 20l-4-4',
  mic: 'M12 15a3 3 0 003-3V6a3 3 0 10-6 0v6a3 3 0 003 3zM19 11a7 7 0 01-14 0M12 18v3',
  play: 'M8 5.5v13l10.5-6.5z',
  pause: 'M9 5v14M15 5v14',
  thumbtack:
    'M12 17v5M5 17h14v-1.76a2 2 0 00-1.11-1.79l-1.78-.9A2 2 0 0115 10.76V6h1a2 2 0 000-4H8a2 2 0 000 4h1v4.76a2 2 0 01-1.11 1.79l-1.78.9A2 2 0 005 15.24z',
  people: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.9M16 3.1a4 4 0 010 7.8',
  bell: 'M18 8a6 6 0 00-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 01-3.4 0',
  receipt: 'M5 2h14v20l-3-2-2 2-2-2-2 2-2-2-3 2zM9 7h6M9 11h6M9 15h4',
  board: 'M9 2h6a1 1 0 011 1v2H8V3a1 1 0 011-1zM8 4H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-2M9 12h6M9 16h4',
  gift: 'M20 12v10H4V12M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z',
  barcode: 'M3 5v14M6 5v14M10 5v14M13 5v14M17 5v14M21 5v14',
  bookmark: 'M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z',
  lock: 'M5 11h14v10H5zM8 11V7a4 4 0 018 0v4',
  phone:
    'M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.5 19.5 0 01-6-6A19.8 19.8 0 012.1 4.2 2 2 0 014.1 2h3a2 2 0 012 1.7c.1.9.4 1.8.7 2.7a2 2 0 01-.5 2.1L8 9.8a16 16 0 006 6l1.3-1.3a2 2 0 012.1-.4c.9.3 1.8.6 2.7.7a2 2 0 011.7 2z',
  phoneOff:
    'M10.7 13.3a16 16 0 003.4 2.6l1.3-1.3a2 2 0 012.1-.4c.9.3 1.8.6 2.7.7a2 2 0 011.7 2v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.4 19.4 0 01-3.3-2.7m-2.7-3.3a19.8 19.8 0 01-3.1-8.7A2 2 0 014.1 2h3a2 2 0 012 1.7c.1.9.4 1.8.7 2.7a2 2 0 01-.5 2.1L8 9.8M23 1L1 23',
  video: 'M23 7l-7 5 7 5V7zM3 5h11a2 2 0 012 2v10a2 2 0 01-2 2H3a2 2 0 01-2-2V7a2 2 0 012-2z',
  videoOff: 'M16 16v1a2 2 0 01-2 2H3a2 2 0 01-2-2V7a2 2 0 012-2h2m5.7 0H14a2 2 0 012 2v3.3l1 1L23 7v10M1 1l22 22',
  micOff: 'M1 1l22 22M9 9v3a3 3 0 005.1 2.1M15 9.3V4a3 3 0 00-5.9-.6M17 16.9A7 7 0 015 12v-2m14 0v2a7 7 0 01-.1 1.2M12 19v4M8 23h8',
  monitor: 'M3 3h18a2 2 0 012 2v10a2 2 0 01-2 2H3a2 2 0 01-2-2V5a2 2 0 012-2zM8 21h8M12 17v4',
  rotate: 'M23 4v6h-6M1 20v-6h6M3.5 9a9 9 0 0114.9-3.4L23 10M1 14l4.6 4.4A9 9 0 0020.5 15',
  'check-circle': 'M22 11.1V12a10 10 0 11-5.9-9.1M22 4L12 14l-3-3',
  // board marks (design 01b 3): 사용함 a coupon with a check, 완료 a check in a square
  'coupon-check': 'M4 6h16a1 1 0 011 1v3a2 2 0 000 4v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3a2 2 0 000-4V7a1 1 0 011-1zM8.5 12l2.5 2.5 4.5-5',
  'check-square': 'M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2zM8 12l3 3 5-6',
  undo: 'M3 7v6h6M21 17a9 9 0 00-15-6.7L3 13',
  up: 'M18 15l-6-6-6 6',
  grip: 'M9 5h.01M15 5h.01M9 12h.01M15 12h.01M9 19h.01M15 19h.01',
  note: 'M4 6h16M4 12h16M4 18h10',
  'take-down': 'M12 4v11M7 10l5 5 5-5M5 20h14',
  clock: 'M12 22a10 10 0 100-20 10 10 0 000 20zM12 6v6l4 2',
  won: 'M3 6h18v12H3zM12 15a3 3 0 100-6 3 3 0 000 6zM6.5 12h.01M17.5 12h.01',
  bag: 'M6 8h12l1 13H5zM9 8V6a3 3 0 016 0v2',
  smile: 'M12 22a10 10 0 100-20 10 10 0 000 20zM8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01',
  link: 'M10 13a5 5 0 007.5.5l3-3a5 5 0 00-7-7l-1.7 1.7M14 11a5 5 0 00-7.5-.5l-3 3a5 5 0 007 7l1.7-1.7',
  info: 'M12 22a10 10 0 100-20 10 10 0 000 20zM12 16v-5M12 8h.01',
  volume: 'M11 5L6 9H2v6h4l5 4zM15.5 8.5a5 5 0 010 7M19 5a10 10 0 010 14',
} as const

export type IconName = keyof typeof ICONS

/** Simple line icons (no icon font or extra requests). */
export function Icon({ name, size = 22 }: { name: IconName; size?: number }) {
  return (
    <svg className="icon" viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={name === 'more' || name === 'grip' ? 3 : 2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d={ICONS[name]} />
    </svg>
  )
}

/** A bottom sheet (phone) / centered dialog (PC). Back button closes it. */
export function Sheet(props: { open: boolean; onClose: () => void; title?: string; children: ReactNode }) {
  useBackToClose(props.open, props.onClose)
  const ref = useRef<HTMLDivElement>(null)
  const closeRef = useRef(props.onClose)
  closeRef.current = props.onClose
  // Focus the sheet when it opens, not each time the screen under it draws
  // again (a new message, a change): that would take the focus from what
  // is being typed in the sheet and close a phone's keyboard.
  useEffect(() => {
    if (!props.open) return
    const onKey = (e: KeyboardEvent) => {
      // what is inside may use Escape first (taking back a table cell's edit)
      if (e.key === 'Escape' && !e.defaultPrevented) closeRef.current()
    }
    window.addEventListener('keydown', onKey)
    ref.current?.focus()
    return () => window.removeEventListener('keydown', onKey)
  }, [props.open])
  if (!props.open) return null
  return (
    <div className="sheet-backdrop" onClick={props.onClose}>
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={props.title}
        tabIndex={-1}
        ref={ref}
        onClick={(e) => e.stopPropagation()}
      >
        {props.title && <div className="sheet-title">{props.title}</div>}
        {props.children}
      </div>
    </div>
  )
}

/** A labelled input. The hint is linked with aria-describedby, so it is
    read out but is not part of the input's name. */
export function Field(props: { label: string; children: ReactElement<{ id?: string; 'aria-describedby'?: string }>; hint?: string }) {
  const id = useId()
  const hintId = `${id}-hint`
  const input = isValidElement(props.children)
    ? cloneElement(props.children, { id, ...(props.hint ? { 'aria-describedby': hintId } : {}) })
    : props.children
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {props.label}
      </label>
      {input}
      {props.hint && (
        <span className="field-hint" id={hintId}>
          {props.hint}
        </span>
      )}
    </div>
  )
}

export function ErrorText({ text }: { text: string | null }) {
  if (!text) return null
  return (
    <p className="error" role="alert">
      {text}
    </p>
  )
}

export function Spinner({ label = '불러오는 중' }: { label?: string }) {
  return (
    <div className="spinner" role="status" aria-label={label}>
      <span />
    </div>
  )
}
